import React, { useState, useEffect, useRef } from 'react';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import { EMPLOYMENT_TYPE_OPTIONS } from '../utils/constants';
import EmployeeService from '../services/EmployeeService';
import httpService from '../../../core/services/HttpService';
import {
  getCompensation,
  createCompensationPeriod,
  replaceCompensationPeriod,
  CompensationType,
} from '../services/CompensationService';
import { FaPlus, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatDaysHalf } from '../utils/leaveFormat';
import { statutoryDaysPerYearFromPattern } from '../utils/leaveStatutory';
import { monthsEmployedInYear } from '../utils/leaveCalc';
import { toNullableDecimalString, parseDecimalString } from '../utils/leaveHelpers';

import { NewContractForm } from './NewContractForm';
import { ContractPeriodCard, DataRow, DashedSeparator, formatDateDE, getEmploymentLabel } from './ContractPeriodCard';
import styles from './ContractHistoryTimeline.module.css';

interface ContractHistoryTimelineProps {
  profile: any;
  employeeId: number;
  refetchProfile: () => Promise<void>;
  onLeaveChange?: (leave: any) => void;
  onProfileChange?: (updater: (prev: any) => any) => void;
  onPendingCompChange?: (comp: { type: CompensationType; amount: string; valid_from: string } | null) => void;
  workAreas?: Array<{ id: number; name: string }>;
  highlightField?: string | null;
  onHighlightDone?: () => void;
}

const ContractHistoryTimeline: React.FC<ContractHistoryTimelineProps> = ({
  profile, employeeId, refetchProfile, onLeaveChange, onProfileChange,
  onPendingCompChange, workAreas = [], highlightField, onHighlightDone,
}) => {
  const [overview, setOverview] = useState<any>(null);
  const [compensation, setCompensation] = useState<any>(null);
  const [compLocked, setCompLocked] = useState(false);
  const hasActiveContract = !!profile?.job?.start_date;
  const [showNewForm, setShowNewForm] = useState(!hasActiveContract);
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(new Set());
  const startDateInputRef = useRef<HTMLDivElement>(null);
  const workAreaInputRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    start_date: '', position_title: '', employment_type: '', work_area: '',
    hours_per_week: '', days_distribution: ['', '', '', '', '', '', ''] as string[],
    regular_workdays: [0, 0, 0, 0, 0, 0, 0] as number[],
    contract_end_date: '', probation_months: '6', extra_leave_days: '',
    comp_type: 'HOURLY' as CompensationType, comp_amount: '',
  });

  const [showCompForm, setShowCompForm] = useState(false);
  const [compForm, setCompForm] = useState({ type: 'HOURLY' as CompensationType, amount: '', valid_from: '' });
  const [compSaving, setCompSaving] = useState(false);
  const [contracts, setContracts] = useState<any[]>([]);
  const [editingContract, setEditingContract] = useState(false);

  useEffect(() => {
    let alive = true;
    async function loadOverview() {
      try { const data = await EmployeeService.getEmployeeOverview(employeeId); if (alive) setOverview(data); } catch { /* optional */ }
    }
    async function loadCompensation() {
      try { const data = await getCompensation(employeeId); if (alive) setCompensation(data); } catch (e: any) { const status = e?.response?.status; if (status === 403 || status === 423) { if (alive) setCompLocked(true); } }
    }
    async function loadContracts() {
      try {
        const res = await httpService.get<any>(`/api/admin/employees/${employeeId}/contracts/`);
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (alive) setContracts(data);
      } catch {
        try {
          const res = await httpService.get<any>('/api/employee-contracts/');
          const all = Array.isArray(res.data) ? res.data : res.data?.results || [];
          if (alive) setContracts(all.filter((c: any) => c.employee === employeeId));
        } catch { /* optional */ }
      }
    }
    loadOverview(); loadCompensation(); loadContracts();
    return () => { alive = false; };
  }, [employeeId]);

  useEffect(() => {
    const refMap: Record<string, React.RefObject<HTMLDivElement>> = { start_date: startDateInputRef, primary_work_area: workAreaInputRef };
    const ref = highlightField ? refMap[highlightField] : null;
    if (ref?.current) {
      if (!showNewForm) setShowNewForm(true);
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current?.classList.add('field-highlight');
        setTimeout(() => { ref.current?.classList.remove('field-highlight'); onHighlightDone?.(); }, 2000);
      }, 150);
    }
  }, [highlightField]);

  useEffect(() => {
    if (editingContract && compForm.amount) {
      onPendingCompChange?.({ type: compForm.type, amount: compForm.amount, valid_from: compForm.valid_from || profile?.job?.start_date || new Date().toISOString().slice(0, 10) });
    } else {
      onPendingCompChange?.(null);
    }
  }, [editingContract, compForm.type, compForm.amount, compForm.valid_from]);

  const reloadContracts = async () => {
    try { const res = await httpService.get<any>(`/api/admin/employees/${employeeId}/contracts/`); setContracts(Array.isArray(res.data) ? res.data : res.data?.results || []); }
    catch { try { const res = await httpService.get<any>('/api/employee-contracts/'); const all = Array.isArray(res.data) ? res.data : res.data?.results || []; setContracts(all.filter((c: any) => c.employee === employeeId)); } catch { /* optional */ } }
  };
  const reloadOverview = async () => { try { setOverview(await EmployeeService.getEmployeeOverview(employeeId)); } catch { /* optional */ } };
  const reloadCompensation = async () => { try { setCompensation(await getCompensation(employeeId)); } catch (e: any) { if (e?.response?.status === 403 || e?.response?.status === 423) setCompLocked(true); } };

  const startEditing = () => {
    const curr = compensation?.current;
    if (curr) {
      setCompForm({
        type: curr.compensation_type === 'SALARY' ? 'SALARY' : 'HOURLY',
        amount: curr.compensation_type === 'SALARY'
          ? ((curr.salary_monthly_cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })
          : parseFloat(curr.hourly_rate || '0').toLocaleString('de-DE', { minimumFractionDigits: 2 }),
        valid_from: curr.valid_from || '',
      });
    }
    setEditingContract(true);
  };
  const cancelEditing = () => { setEditingContract(false); setCompForm({ type: 'HOURLY', amount: '', valid_from: '' }); refetchProfile(); };

  const updateJob = (field: string, value: any) => { onProfileChange?.((prev: any) => ({ ...prev, job: { ...prev.job, [field]: value } })); };
  const updateWorkArea = (waId: string) => {
    if (!waId) return;
    const id = parseInt(waId);
    const waDetail = workAreas.find(wa => wa.id === id);
    onProfileChange?.((prev: any) => ({ ...prev, job: { ...prev.job, primary_work_area: id, work_areas: [id], work_areas_detail: waDetail ? [waDetail] : prev.job?.work_areas_detail } }));
  };
  const updateWorkingPattern = (field: string, value: any) => { onProfileChange?.((prev: any) => ({ ...prev, working_pattern: { ...prev.working_pattern, [field]: value } })); };

  const handleSaveNewPeriod = async () => {
    if (!newPeriod.start_date) { toast.error('Startdatum ist erforderlich'); return; }
    setSaving(true);
    try {
      const patch: any = { job: { start_date: newPeriod.start_date, ...(newPeriod.position_title && { position_title: newPeriod.position_title }), ...(newPeriod.employment_type && { employment_type: newPeriod.employment_type }), ...(newPeriod.contract_end_date && { contract_end_date: newPeriod.contract_end_date }), ...(newPeriod.work_area && { work_areas: [parseInt(newPeriod.work_area)], primary_work_area: parseInt(newPeriod.work_area) }) } };
      if (newPeriod.hours_per_week) { patch.working_pattern = { hours_per_week: parseFloat(newPeriod.hours_per_week) }; }
      const hasDistribution = newPeriod.days_distribution.some(v => v !== '');
      if (hasDistribution) { const dist = newPeriod.days_distribution.map(v => v ? parseFloat(v) : 0); if (!patch.working_pattern) patch.working_pattern = {}; patch.working_pattern.days_distribution = dist; const totalFromDist = dist.reduce((s, v) => s + v, 0); if (!newPeriod.hours_per_week && totalFromDist > 0) patch.working_pattern.hours_per_week = totalFromDist; }
      const newRegularWd = newPeriod.regular_workdays;
      const existingRegularWd: number[] = profile?.working_pattern?.regular_workdays;
      const regularWd = newRegularWd.some(v => v > 0) ? newRegularWd : existingRegularWd;
      if (regularWd?.length === 7 && regularWd.some(v => v > 0)) { if (!patch.working_pattern) patch.working_pattern = {}; patch.working_pattern.regular_workdays = regularWd; }
      if (newPeriod.probation_months) { const months = parseInt(newPeriod.probation_months); if (months > 0) { const start = new Date(newPeriod.start_date); start.setMonth(start.getMonth() + months); patch.job.probation_end_date = start.toISOString().split('T')[0]; } }
      if (newPeriod.extra_leave_days) { patch.leave = { holiday_extra_days_per_year: parseFloat(newPeriod.extra_leave_days) }; }
      await EmployeeService.updateEmployeeProfile(employeeId, patch);
      if (newPeriod.comp_amount && newPeriod.start_date) {
        try {
          const amountStr = newPeriod.comp_amount.replace(',', '.');
          let compPayload: any;
          if (newPeriod.comp_type === 'HOURLY') { compPayload = { compensation_type: 'HOURLY' as CompensationType, hourly_rate: amountStr, currency: 'EUR' as const, valid_from: newPeriod.start_date }; }
          else { compPayload = { compensation_type: 'SALARY' as CompensationType, salary_monthly_cents: Math.round(parseFloat(amountStr) * 100), currency: 'EUR' as const, valid_from: newPeriod.start_date }; }
          try { await createCompensationPeriod(employeeId, compPayload); } catch (ce: any) { if (ce?.response?.status === 409) await replaceCompensationPeriod(employeeId, compPayload); }
          await reloadCompensation();
        } catch { toast.warn('Profil gespeichert, aber Verguetung konnte nicht angelegt werden'); }
      }
      await refetchProfile();
      await Promise.all([reloadContracts(), reloadOverview()]);
      setShowNewForm(false);
      setNewPeriod({ start_date: '', position_title: '', employment_type: '', work_area: '', hours_per_week: '', days_distribution: ['', '', '', '', '', '', ''], regular_workdays: [0, 0, 0, 0, 0, 0, 0], contract_end_date: '', probation_months: '6', extra_leave_days: '', comp_type: 'HOURLY', comp_amount: '' });
      toast.success('Vertrag angelegt');
    } catch (e: any) {
      const status = e?.status || e?.response?.status;
      const data = e?.data || e?.response?.data;
      let msg = data?.message || data?.error || e?.message || 'Fehler beim Speichern';
      if (data?.details) msg += ` (${Object.keys(data.details).join(', ')})`;
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleSaveCompensation = async () => {
    if (!compForm.valid_from) { toast.error('Gueltig ab ist erforderlich'); return; }
    if (!compForm.amount) { toast.error('Betrag ist erforderlich'); return; }
    setCompSaving(true);
    try {
      const amountStr = compForm.amount.replace(',', '.');
      let payload: any;
      if (compForm.type === 'HOURLY') { payload = { compensation_type: 'HOURLY' as CompensationType, hourly_rate: amountStr, currency: 'EUR' as const, valid_from: compForm.valid_from }; }
      else { payload = { compensation_type: 'SALARY' as CompensationType, salary_monthly_cents: Math.round(parseFloat(amountStr) * 100), currency: 'EUR' as const, valid_from: compForm.valid_from }; }
      try { await createCompensationPeriod(employeeId, payload); } catch (e: any) { if (e?.response?.status === 409 && e?.response?.data?.error === 'period_exists') await replaceCompensationPeriod(employeeId, payload); else throw e; }
      await reloadCompensation();
      setShowCompForm(false);
      setCompForm({ type: 'HOURLY', amount: '', valid_from: '' });
      toast.success('Verguetung gespeichert');
    } catch (e: any) { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Fehler beim Speichern'); }
    finally { setCompSaving(false); }
  };

  /* Derive current contract data */
  const startDate = profile?.job?.start_date;
  const endDate = profile?.job?.contract_end_date;
  const positionTitle = profile?.job?.position_title || '\u2013';
  const department = profile?.job?.work_areas_detail?.[0]?.name || '\u2013';
  const employmentType = profile?.job?.employment_type;
  const hoursPerWeek = profile?.working_pattern?.hours_per_week;
  const days: number[] = profile?.working_pattern?.days_distribution || [];
  const regularWd: number[] = profile?.working_pattern?.regular_workdays || [];
  const wdFromRegular = regularWd.length === 7 ? regularWd.filter((v: number) => v > 0).length : 0;
  const statutoryBE = profile?.leave?.statutory_days_per_year ?? null;
  const statutory = statutoryBE ?? (wdFromRegular > 0 ? wdFromRegular * 4 : statutoryDaysPerYearFromPattern(days));
  const extra = Number(profile?.leave?.holiday_extra_days_per_year ?? 0);
  const totalLeavePerYearCalc = statutory != null ? statutory + extra : null;
  const totalLeavePerYear = totalLeavePerYearCalc;
  const year = new Date().getFullYear();
  const monthsEmployed = monthsEmployedInYear(startDate || null, endDate || null, year);
  const proRata = totalLeavePerYear != null ? (totalLeavePerYear * monthsEmployed) / 12 : null;
  const leaveTaken = overview?.leave_balance?.used ?? null;
  const leaveAvailableBE = overview?.leave_balance?.available ?? null;
  const leaveRemaining = leaveAvailableBE ?? (proRata != null && leaveTaken != null ? proRata - leaveTaken : null);
  const overtimeCarryover = profile?.payroll?.opening_balance_h;
  const compHistory = compensation?.history || compensation?.periods || [];

  /* Merge employment history */
  const contractHistory: any[] = contracts
    .filter((c: any) => { const hasEnded = c.valid_until || c.end_date; const cStart = c.valid_from || c.start_date; return hasEnded && cStart !== startDate; })
    .map((c: any) => ({ start_date: c.valid_from || c.start_date, end_date: c.valid_until || c.end_date || c.contract_end_date, employment_type: c.employment_type, position_title: c.position_title, hours_per_week: c.hours_per_week || c.weekly_hours, department: c.work_area_name || c.department }));
  const rawHistory: any[] = (overview?.employment_history || []).filter((h: any) => { const hStart = h.start_date || h.valid_from; return hStart && hStart !== startDate; });
  const seen = new Set<string>();
  const employmentHistory: any[] = [];
  for (const entry of [...contractHistory, ...rawHistory]) { const key = entry.start_date || entry.valid_from || ''; if (key && seen.has(key)) continue; if (key) seen.add(key); employmentHistory.push(entry); }
  employmentHistory.sort((a: any, b: any) => (b.start_date || '').localeCompare(a.start_date || ''));

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.sectionLabel}>VERTRAGSHISTORIE</div>
        {(startDate || editingContract) && (
          <button type="button" className="hover-back" onClick={() => setShowNewForm(!showNewForm)} style={{ ...ps.backButton, marginBottom: 0, fontSize: 11, padding: '5px 14px', color: colors.navy }}>
            <FaPlus size={9} /> New Contract
          </button>
        )}
      </div>

      {showNewForm && (
        <NewContractForm
          newPeriod={newPeriod}
          onNewPeriodChange={setNewPeriod}
          workAreas={workAreas}
          positionTitle={positionTitle}
          hoursPerWeek={hoursPerWeek}
          startDate={startDate}
          saving={saving}
          onSave={handleSaveNewPeriod}
          onCancel={() => setShowNewForm(false)}
          startDateInputRef={startDateInputRef}
          workAreaInputRef={workAreaInputRef}
        />
      )}

      <div className={styles.timeline}>
        <div className={styles.timelineLine} />

        <ContractPeriodCard
          profile={profile} startDate={startDate} endDate={endDate}
          positionTitle={positionTitle} department={department}
          employmentType={employmentType} hoursPerWeek={hoursPerWeek}
          days={days} totalLeavePerYear={totalLeavePerYear}
          statutory={statutory} extra={extra} proRata={proRata}
          monthsEmployed={monthsEmployed} year={year}
          leaveTaken={leaveTaken} leaveRemaining={leaveRemaining}
          overtimeCarryover={overtimeCarryover} compHistory={compHistory}
          compensation={compensation} compLocked={compLocked}
          editingContract={editingContract} showCompForm={showCompForm}
          compForm={compForm} compSaving={compSaving} workAreas={workAreas}
          onStartEditing={startEditing} onCancelEditing={cancelEditing}
          onSetShowCompForm={setShowCompForm} onCompFormChange={setCompForm}
          onSaveCompensation={handleSaveCompensation}
          onUpdateJob={updateJob} onUpdateWorkArea={updateWorkArea}
          onUpdateWorkingPattern={updateWorkingPattern}
          onLeaveChange={onLeaveChange}
        />

        {/* Past Contracts */}
        {employmentHistory.map((period: any, idx: number) => {
          const isExpanded = expandedHistory.has(idx);
          const toggleExpand = () => { setExpandedHistory(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; }); };
          const periodEnd = period.end_date || period.start_date;
          const activeComp = [...compHistory].filter((c: any) => c.valid_from <= periodEnd).sort((a: any, b: any) => b.valid_from.localeCompare(a.valid_from))[0];
          const compLabel = activeComp
            ? (activeComp.compensation_type === 'SALARY' ? `${((activeComp.salary_monthly_cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/Mon.` : `${parseFloat(activeComp.hourly_rate || '0').toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/h`)
            : null;
          const summaryParts = [getEmploymentLabel(period.employment_type), period.hours_per_week ? `${period.hours_per_week} Std/Woche` : null].filter(Boolean).join(' \u00B7 ');

          return (
            <div key={`hist-${idx}`} className={styles.timelineEntry}>
              <div className={styles.historyDot} />
              <div onClick={toggleExpand} className={styles.historySummary}>
                <span className={styles.historyChevron}>{isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}</span>
                <span className={styles.historyDates}>{formatDateDE(period.start_date)} \u2013 {formatDateDE(period.end_date)}</span>
                {summaryParts && <span className={styles.historySummaryText}>\u00B7 {summaryParts}</span>}
              </div>
              {isExpanded && (
                <div className={styles.historyDetail}>
                  <div className={styles.historyDetailContent}>
                    <DataRow label="Rolle" value={period.position_title || '\u2013'} />
                    {period.department && <DataRow label="Abteilung" value={period.department} />}
                    <DataRow label="Typ" value={getEmploymentLabel(period.employment_type)} />
                    <DataRow label="Stunden" value={period.hours_per_week ? `${period.hours_per_week} Std/Woche` : '\u2013'} />
                    {compLabel && <DataRow label="Verguetung" value={compLabel} />}
                  </div>
                  <DashedSeparator />
                  <div className={styles.historyDetailContent}>
                    <DataRow label="Genommen" value="\u2013" />
                    <DataRow label="Verbleibend" value="\u2013" />
                  </div>
                  <DashedSeparator />
                  <div className={styles.subLabel}>UEBERSTUNDENKONTO</div>
                  <div className={styles.historyDetailContent}>
                    <DataRow label="Saldo" value="\u2013" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContractHistoryTimeline;
